import { motion } from "framer-motion";
import { Network, Zap, Building, Server, Webhook, ArrowRight, Radio, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";
import { Link } from "react-router-dom";
import dermsIntegrationImage from "@/assets/derms-integration.png";

const integrationProtocols = [
  {
    icon: Radio,
    name: "MQTT & Apache Kafka",
    description: "Real-time streaming for high-throughput data exchange",
  },
  {
    icon: Zap,
    name: "OpenADR 2.0",
    description: "Certified demand response automation standard",
  },
  {
    icon: Server,
    name: "REST API",
    description: "Push/Pull integration for flexible data access",
  },
  {
    icon: Webhook,
    name: "Webhooks",
    description: "Event-driven notifications for real-time updates",
  },
];

const utilityBenefits = [
  "Seamless DERMS integration for distributed energy management",
  "Real-time load management and demand response",
  "Grid stability support through V2G discharge",
  "Automated utility program participation",
  "Energy market revenue optimization",
  "Certified OpenADR 2.0 compliance",
];

export function DERMSIntegration() {
  return (
    <section className="py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-card/30 via-background to-card/20" />
      <div className="absolute inset-0">
        <motion.div 
          className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-primary/6 rounded-full blur-[150px]"
          animate={{ 
            x: [0, 30, 0],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Section Divider */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/50 to-transparent" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <ScrollReveal className="text-center mb-20">
          <span className="inline-flex items-center gap-2 glass-card px-5 py-2.5 rounded-full text-sm font-medium text-primary mb-8">
            <Network className="h-4 w-4" />
            Utility & Grid Integration
          </span>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
            DERMS & <span className="text-gradient">Utility Integrations</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Connect your EV charging infrastructure directly to utility systems and DERMS platforms 
            through our comprehensive message bus architecture.
          </p>
        </ScrollReveal>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left - Integration Diagram */}
          <ScrollReveal>
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="relative rounded-3xl overflow-hidden border border-border/30 shadow-2xl glass-card p-3"
            >
              <img
                src={dermsIntegrationImage}
                alt="DERMS Integration Architecture - EvGateway connects to utilities and DERMS via MQTT, OpenADR 2.0, REST API, and Webhooks"
                className="w-full h-auto rounded-2xl"
              />
              
              {/* Animated connection indicator */}
              <div className="absolute inset-0 pointer-events-none">
                <motion.div
                  className="absolute top-1/2 left-1/4 w-3 h-3 bg-primary rounded-full shadow-lg shadow-primary/50"
                  animate={{ 
                    x: [0, 120, 240],
                    opacity: [1, 0.7, 0],
                    scale: [1, 0.8, 0.5]
                  }}
                  transition={{ 
                    duration: 2.5, 
                    repeat: Infinity,
                    ease: "easeOut"
                  }}
                />
              </div>
            </motion.div>
          </ScrollReveal>

          {/* Right - Content */}
          <ScrollReveal delay={0.2}>
            <div className="space-y-8">
              <div>
                <span className="text-primary font-semibold uppercase tracking-widest text-sm">Message Bus Architecture</span>
                <h3 className="text-3xl md:text-4xl font-bold mt-3 mb-5">
                  Enterprise-Grade Utility Connectivity
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Our platform provides seamless integration with utility systems and DERMS platforms 
                  through a robust message bus architecture. Connect your charging infrastructure to 
                  participate in demand response programs, grid services, and energy markets.
                </p>
              </div>

              {/* Benefits List */}
              <div className="space-y-4">
                {utilityBenefits.map((benefit, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4 group"
                  >
                    <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Zap className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-foreground/90 font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <Button variant="hero" asChild className="group">
                  <Link to="/contact">
                    Discuss Integration
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* Protocol Cards */}
        <ScrollReveal>
          <h3 className="text-2xl font-bold text-center mb-10">Supported Integration Protocols</h3>
        </ScrollReveal>
        
        <StaggerContainer className="grid grid-cols-2 lg:grid-cols-4 gap-6" staggerDelay={0.1}>
          {integrationProtocols.map((protocol, idx) => (
            <StaggerItem key={idx}>
              <motion.div
                whileHover={{ scale: 1.03, y: -6 }}
                className="glass-card rounded-3xl p-8 text-center group cursor-default"
              >
                <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <protocol.icon className="h-7 w-7 text-primary" />
                </div>
                <h4 className="font-bold mb-2 text-lg">{protocol.name}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{protocol.description}</p>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* Bottom CTA */}
        <ScrollReveal delay={0.4} className="mt-20">
          <motion.div 
            className="glass-card rounded-3xl p-10 md:p-12 text-center"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-center gap-6 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Building className="h-8 w-8 text-primary" />
              </div>
              <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center">
                <Globe className="h-8 w-8 text-accent" />
              </div>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-4">Ready to Connect Your Infrastructure?</h3>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Our team of integration specialists will work with you to seamlessly connect 
              your charging network to utility systems and DERMS platforms.
            </p>
            <Button variant="hero" size="lg" asChild className="group">
              <a href="https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/" target="_blank" rel="noopener noreferrer">
                Schedule Integration Consultation
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>
          </motion.div>
        </ScrollReveal>
      </div>
    </section>
  );
}
